// The component kernel's stylesheet, generated from tokens.
//
// Why a stylesheet rather than inline styles: inline styles cannot express
// `:focus-visible`, `:hover`, `@media (prefers-reduced-motion)` or forced-colours
// overrides — and those are exactly the things this product must get right. Class
// names keep the components framework-agnostic; the CSS is a plain string a consumer
// drops into a <style> tag alongside tokenStylesheet().
//
// EVERY value here comes from a token. No raw colour or spacing literal appears in a
// component or in this file — `noRawLiterals` in styles.test.ts enforces that
// mechanically, because "use the tokens" is a rule people follow until the day they
// are in a hurry.

import {
  color,
  elevation,
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  motion,
  pastDisplay,
  radius,
  space,
  stateColor,
  targetSize,
} from "../tokens";

/** Class names are exported so components never spell them as bare strings. */
export const cx = {
  button: "hf-button",
  buttonPrimary: "hf-button--primary",
  buttonSecondary: "hf-button--secondary",
  buttonDanger: "hf-button--danger",

  field: "hf-field",
  fieldLabel: "hf-field__label",
  fieldControl: "hf-field__control",
  fieldHint: "hf-field__hint",
  fieldError: "hf-field__error",

  card: "hf-card",
  cardTitle: "hf-card__title",

  chip: "hf-chip",
  chipDot: "hf-chip__dot",

  dialogBackdrop: "hf-dialog__backdrop",
  dialog: "hf-dialog",
  dialogTitle: "hf-dialog__title",

  status: "hf-status",
  statusInfo: "hf-status--info",
  statusSuccess: "hf-status--success",
  statusWarning: "hf-status--warning",
  statusError: "hf-status--error",

  emptyState: "hf-empty",
  skeleton: "hf-skeleton",
  skeletonLine: "hf-skeleton__line",
  visuallyHidden: "hf-visually-hidden",

  calendarWeek: "hf-week",
  calendarDay: "hf-day",
  calendarDayHeader: "hf-day__header",
  calendarLane: "hf-lane",
  calendarLaneLabel: "hf-lane__label",
  calendarLaneItems: "hf-lane__items",
} as const;

const stateChipRules = Object.entries(stateColor)
  .map(([key, token]) => {
    const modifier = key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
    return `.${cx.chip}--${modifier} {
  color: ${token.fg};
  background: ${token.bg};
  border-color: ${token.border};
}`;
  })
  .join("\n\n");

export function componentStylesheet(): string {
  return `/* Here For component kernel. Generated from tokens — do not hand-edit. */

/* Focus is never removed, only restyled. A two-tone ring so it is perceivable on
   any background, including the accent and danger buttons. */
.${cx.button}:focus-visible,
.${cx.fieldControl}:focus-visible,
.${cx.chip}:focus-visible,
.${cx.calendarLane}:focus-visible {
  outline: ${focusRing.width} solid transparent;
  outline-offset: ${focusRing.offset};
  box-shadow: ${focusRing.style};
}

.${cx.button} {
  /* Target size: a floor, not the aspiration (plan 24.2). */
  min-height: ${targetSize.minimum};
  min-width: ${targetSize.minimum};
  padding: ${space[2]} ${space[4]};
  border-radius: ${radius.md};
  border: 1px solid transparent;
  font-size: ${fontSize.base};
  font-weight: ${String(fontWeight.medium)};
  line-height: ${String(lineHeight.normal)};
  cursor: pointer;
  transition: background-color ${motion.durationFast} ${motion.easingStandard};
}

.${cx.button}[disabled] {
  cursor: not-allowed;
  opacity: 0.55;
}

.${cx.buttonPrimary} {
  background: ${color.accent};
  color: ${color.textOnAccent};
}

.${cx.buttonPrimary}:hover:not([disabled]) {
  background: ${color.accentHover};
}

.${cx.buttonSecondary} {
  background: ${color.surface};
  color: ${color.textPrimary};
  border-color: ${color.borderStrong};
}

.${cx.buttonDanger} {
  background: ${color.danger};
  color: ${color.textOnAccent};
}

.${cx.field} {
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
}

.${cx.fieldLabel} {
  font-size: ${fontSize.sm};
  font-weight: ${String(fontWeight.medium)};
  color: ${color.textPrimary};
}

.${cx.fieldControl} {
  min-height: ${targetSize.minimum};
  padding: ${space[2]} ${space[3]};
  border: 1px solid ${color.borderStrong};
  border-radius: ${radius.md};
  background: ${color.surface};
  color: ${color.textPrimary};
  font-size: ${fontSize.base};
  line-height: ${String(lineHeight.normal)};
}

.${cx.fieldHint} {
  font-size: ${fontSize.sm};
  color: ${color.textMuted};
}

.${cx.fieldError} {
  font-size: ${fontSize.sm};
  color: ${color.danger};
  font-weight: ${String(fontWeight.medium)};
}

.${cx.card} {
  background: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.lg};
  padding: ${space[4]};
  box-shadow: ${elevation.raised};
}

.${cx.cardTitle} {
  font-size: ${fontSize.lg};
  font-weight: ${String(fontWeight.semibold)};
  line-height: ${String(lineHeight.tight)};
  color: ${color.textPrimary};
  margin: 0 0 ${space[2]};
}

.${cx.chip} {
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[1]} ${space[3]};
  border: 1px solid;
  border-radius: ${radius.pill};
  font-size: ${fontSize.sm};
  font-weight: ${String(fontWeight.medium)};
  line-height: ${String(lineHeight.normal)};
}

/* A non-colour channel for the state: shape as well as hue, so meaning survives
   greyscale printing, colour blindness and forced-colours modes (plan 24.6). */
.${cx.chipDot} {
  width: ${space[2]};
  height: ${space[2]};
  border-radius: ${radius.pill};
  background: currentColor;
  flex: none;
}

${stateChipRules}

.${cx.chip}--past {
  color: ${pastDisplay.fg};
  background: ${pastDisplay.bg};
  border-color: ${pastDisplay.border};
}

.${cx.dialogBackdrop} {
  position: fixed;
  inset: 0;
  background: rgba(23, 33, 29, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space[4]};
}

.${cx.dialog} {
  background: ${color.surface};
  border-radius: ${radius.lg};
  box-shadow: ${elevation.overlay};
  padding: ${space[5]};
  max-width: ${layout.dialogMaxWidth};
  width: 100%;
}

.${cx.dialogTitle} {
  font-size: ${fontSize.xl};
  font-weight: ${String(fontWeight.semibold)};
  line-height: ${String(lineHeight.tight)};
  margin: 0 0 ${space[3]};
}

.${cx.status} {
  display: flex;
  gap: ${space[2]};
  padding: ${space[3]};
  border-radius: ${radius.md};
  border: 1px solid;
  font-size: ${fontSize.base};
  line-height: ${String(lineHeight.normal)};
}

.${cx.statusInfo} {
  color: ${color.textPrimary};
  background: ${color.surfaceSubtle};
  border-color: ${color.borderStrong};
}

.${cx.statusSuccess} {
  color: ${stateColor.covered.fg};
  background: ${stateColor.covered.bg};
  border-color: ${stateColor.covered.border};
}

.${cx.statusWarning} {
  color: ${stateColor.pleaseRecheck.fg};
  background: ${stateColor.pleaseRecheck.bg};
  border-color: ${stateColor.pleaseRecheck.border};
}

.${cx.statusError} {
  color: ${color.danger};
  background: ${color.surface};
  border-color: ${color.danger};
}

.${cx.emptyState} {
  text-align: center;
  padding: ${space[6]} ${space[4]};
  color: ${color.textMuted};
}

.${cx.skeleton} {
  background: ${color.surfaceSubtle};
  border-radius: ${radius.sm};
  animation: hf-skeleton-pulse ${motion.durationSlow} ${motion.easingStandard} infinite alternate;
}

.${cx.skeletonLine} {
  height: ${fontSize.base};
  margin-bottom: ${space[2]};
}

/* Available to assistive technology, invisible on screen. Never display:none —
   that would hide it from screen readers too, which is the opposite of the point. */
.${cx.visuallyHidden} {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

@keyframes hf-skeleton-pulse {
  from { opacity: 1; }
  to { opacity: 0.6; }
}

/* Reduced motion is honoured here, not left to each component author to remember. */
@media (prefers-reduced-motion: reduce) {
  .${cx.skeleton} {
    animation: none;
  }

  .${cx.button} {
    transition: none;
  }
}

.${cx.calendarWeek} {
  display: grid;
  gap: ${space[3]};
}

.${cx.calendarDay} {
  border: 1px solid ${color.border};
  border-radius: ${radius.md};
  background: ${color.surface};
  padding: ${space[3]};
}

.${cx.calendarDayHeader} {
  font-size: ${fontSize.sm};
  font-weight: ${String(fontWeight.semibold)};
  color: ${color.textPrimary};
  margin: 0 0 ${space[2]};
}

.${cx.calendarLane} {
  display: grid;
  grid-template-columns: minmax(${layout.laneLabelWidth}, auto) 1fr;
  gap: ${space[3]};
  padding: ${space[2]} 0;
  border-top: 1px solid ${color.border};
}

.${cx.calendarLaneLabel} {
  font-size: ${fontSize.sm};
  color: ${color.textMuted};
}

.${cx.calendarLaneItems} {
  display: flex;
  flex-wrap: wrap;
  gap: ${space[2]};
  margin: 0;
  padding: 0;
  list-style: none;
}
`;
}
